import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { SafeIcon } from './UI';
import { FiX } from 'react-icons/fi';

interface ModalViewportMetrics {
  height: number | null;
  top: number;
}

const readModalViewport = (): ModalViewportMetrics => {
  if (typeof window === 'undefined') return { height: null, top: 0 };

  const viewport = window.visualViewport;
  if (!viewport) return { height: null, top: 0 };

  const documentScrollTop = window.scrollY || document.documentElement.scrollTop || 0;
  const pageRelativeTop = Math.max(0, Number(viewport.pageTop || 0) - documentScrollTop);
  const visualTop = Math.max(0, Number(viewport.offsetTop || 0), pageRelativeTop);

  const viewportHeights = [
    Number(viewport.height || 0),
    Number(window.innerHeight || 0),
    Number(document.documentElement.clientHeight || 0)
  ].filter(height => Number.isFinite(height) && height > 0);

  return {
    height: viewportHeights.length ? Math.max(1, Math.round(Math.min(...viewportHeights))) : null,
    top: Math.round(visualTop)
  };
};

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg' | 'xl';
}

export const Modal = ({ isOpen, onClose, title, children, footer, size = 'lg' }: ModalProps) => {
  const [viewportMetrics, setViewportMetrics] = useState<ModalViewportMetrics>(() => readModalViewport());

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return;

    const viewport = window.visualViewport;
    let frame = 0;
    let settleShort = 0;
    let settleLong = 0;

    const syncViewport = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        setViewportMetrics(readModalViewport());
      });
    };

    const syncViewportWithSettling = () => {
      syncViewport();
      window.clearTimeout(settleShort);
      window.clearTimeout(settleLong);
      settleShort = window.setTimeout(syncViewport, 60);
      settleLong = window.setTimeout(syncViewport, 180);
    };

    syncViewportWithSettling();
    viewport?.addEventListener('resize', syncViewportWithSettling);
    viewport?.addEventListener('scroll', syncViewportWithSettling);
    window.addEventListener('resize', syncViewportWithSettling);
    window.addEventListener('orientationchange', syncViewportWithSettling);
    window.addEventListener('focusin', syncViewportWithSettling);
    window.addEventListener('focusout', syncViewportWithSettling);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.clearTimeout(settleShort);
      window.clearTimeout(settleLong);
      viewport?.removeEventListener('resize', syncViewportWithSettling);
      viewport?.removeEventListener('scroll', syncViewportWithSettling);
      window.removeEventListener('resize', syncViewportWithSettling);
      window.removeEventListener('orientationchange', syncViewportWithSettling);
      window.removeEventListener('focusin', syncViewportWithSettling);
      window.removeEventListener('focusout', syncViewportWithSettling);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const widthMap = {
    md: 'max-w-lg',
    lg: 'max-w-3xl',
    xl: 'max-w-6xl'
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-x-0 top-0 bottom-auto z-[10050] flex items-end justify-center bg-gray-900/50 p-0 backdrop-blur-sm animate-in fade-in duration-200 sm:items-center sm:p-4"
      style={{
        height: viewportMetrics.height ? `min(${viewportMetrics.height}px, 100dvh)` : '100dvh',
        transform: viewportMetrics.top ? `translate3d(0, ${viewportMetrics.top}px, 0)` : undefined
      }}
    >
      <div className={`bg-white rounded-t-2xl shadow-xl w-full min-w-0 ${widthMap[size]} overflow-hidden flex min-h-0 flex-col max-h-[calc(100%_-_env(safe-area-inset-top)_-_0.5rem)] sm:max-h-[calc(100%_-_2rem)] sm:rounded-xl animate-in zoom-in-95 duration-200`}>
        {/* Header */}
        <div className="flex shrink-0 justify-between items-center gap-3 p-3 sm:p-4 border-b border-gray-100">
          <h3 className="min-w-0 break-words text-base sm:text-lg font-semibold text-gray-900 tracking-tight">{title}</h3>
          <button 
            onClick={onClose} 
            className="min-h-11 min-w-11 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-4 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain flex-1 min-w-0 premium-scrollbar">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="invoiceflow-modal-footer shrink-0 p-3 sm:p-4 border-t border-gray-100 bg-gray-50 flex flex-wrap justify-end items-center gap-2 sm:gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};